import { DateTime } from "luxon";
import { translate } from "src/utils/string-table";

import { TimesheetEntry } from "./TimesheetRecord";

/**
 * Helper to generate the current week's ID based on Sunday-Saturday weeks.
 *
 * @example
 * generateWeekId(DateTime.fromISO("2025-07-13")) // "2025-29"
 */
export function generateWeekId(date: DateTime = DateTime.local()): `${string}-${string}` {
  const { weekEnd } = getWeekRangeFromDateTime(date.toISODate());
  return `${weekEnd.year}-${weekEnd.weekNumber.toString().padStart(2, "0")}`;
}

/**
 * Represents a break period with start and end times
 */
export type BreakPeriod = {
  start: DateTime | undefined;
  end: DateTime | undefined;
};

/**
 * Represents a work period with start and end times
 */
export type WorkPeriod = {
  start: DateTime | undefined;
  end: DateTime | undefined;
};

/**
 * Represents a punch entry for break extraction
 */
export type PunchEntry = {
  type: "PUNCH_IN" | "PUNCH_OUT";
  ts: DateTime | string;
  id?: string;
};

/**
 * Result of break extraction from punch sequences
 */
export type BreakExtractionResult = {
  workPeriod: WorkPeriod;
  breakPeriods: BreakPeriod[];
};

const TOTAL_HOURS_DISPLAY_PLACEHOLDER = "--";

/**
 * Extracts work period and break periods from punch sequences.
 *
 * This function analyzes punch entries to determine:
 * - Work start time: First PUNCH_IN
 * - Work end time: Last PUNCH_OUT
 * - Break periods: Middle PUNCH_OUT to subsequent PUNCH_IN pairs
 *
 * Handles incomplete break pairs gracefully and supports multiple break periods.
 * Works with both DateTime objects and ISO string timestamps.
 *
 * @param punches - Array of punch entries with type and timestamp
 * @returns Object containing work period and break periods
 *
 * @example
 * // Basic work day with no breaks
 * const punches = [
 *   { type: "PUNCH_IN", ts: "2023-01-01T08:00:00Z" },
 *   { type: "PUNCH_OUT", ts: "2023-01-01T17:00:00Z" }
 * ];
 * const result = extractBreakPeriodsFromPunches(punches);
 * // result.workPeriod = { start: DateTime, end: DateTime }
 * // result.breakPeriods = []
 *
 * @example
 * // Work day with lunch break
 * const punches = [
 *   { type: "PUNCH_IN", ts: "2023-01-01T08:00:00Z" },
 *   { type: "PUNCH_OUT", ts: "2023-01-01T12:00:00Z" },
 *   { type: "PUNCH_IN", ts: "2023-01-01T13:00:00Z" },
 *   { type: "PUNCH_OUT", ts: "2023-01-01T17:00:00Z" }
 * ];
 * const result = extractBreakPeriodsFromPunches(punches);
 * // result.workPeriod = { start: DateTime(08:00), end: DateTime(17:00) }
 * // result.breakPeriods = [{ start: DateTime(12:00), end: DateTime(13:00) }]
 */
export function extractBreakPeriodsFromPunches(punches: PunchEntry[]): BreakExtractionResult {
  if (!punches || punches.length === 0) {
    return {
      workPeriod: { start: undefined, end: undefined },
      breakPeriods: [],
    };
  }

  // Normalize timestamps to DateTime objects and sort chronologically
  const sortedPunches = punches
    .map((punch) => ({
      ...punch,
      ts: typeof punch.ts === "string" ? DateTime.fromISO(punch.ts) : punch.ts,
    }))
    .sort((a, b) => a.ts.toMillis() - b.ts.toMillis());

  // Separate punch types while maintaining chronological order
  const punchIns = sortedPunches.filter((p) => p.type === "PUNCH_IN");
  const punchOuts = sortedPunches.filter((p) => p.type === "PUNCH_OUT");

  // Extract work period
  const workPeriod: WorkPeriod = {
    start: punchIns.length > 0 ? punchIns[0].ts : undefined,
    end: punchOuts.length > 0 ? punchOuts[punchOuts.length - 1].ts : undefined,
  };

  // Extract break periods
  const breakPeriods: BreakPeriod[] = [];

  // Based on TimesheetView logic:
  // - Break start: All PUNCH_OUT entries except the last one (work end)
  // - Break end: All PUNCH_IN entries except the first one (work start)
  // This handles multiple breaks by pairing them in sequence

  if (punchOuts.length > 1 && punchIns.length > 1) {
    // Break start times: all PUNCH_OUT except the last (work end)
    const breakStartTimes = punchOuts.slice(0, -1);
    // Break end times: all PUNCH_IN except the first (work start)
    const breakEndTimes = punchIns.slice(1);

    // Pair break starts with break ends
    const numBreaks = Math.min(breakStartTimes.length, breakEndTimes.length);
    for (let i = 0; i < numBreaks; i++) {
      breakPeriods.push({
        start: breakStartTimes[i]?.ts,
        end: breakEndTimes[i]?.ts,
      });
    }
  }

  return {
    workPeriod,
    breakPeriods,
  };
}

/**
 * Calculates the total work hours minus break hours for the given records.
 * Returns a formatted string with the hours suffix.
 *
 * @param records - The records to calculate the total hours for.
 */
export function calculateTotalHours(records: Array<TimesheetEntry>): string {
  if (records.length === 0) {
    return TOTAL_HOURS_DISPLAY_PLACEHOLDER;
  }

  const totalHours = records
    .filter((r) => r.type === "TIMECARD")
    .reduce((acc, record) => {
      // The assumption is that the `entries` field is sorted
      const punchInEntries = record.entries.filter((e) => e.type === "PUNCH_IN");
      const punchOutEntries = record.entries.filter((e) => e.type === "PUNCH_OUT");

      // Only count the work between pairs of punch in and punch out entries.
      while (punchInEntries.length > 0 && punchOutEntries.length > 0) {
        const punchIn = punchInEntries.shift();
        const punchOut = punchOutEntries.shift();
        if (!punchIn || !punchOut) {
          return acc;
        }

        const startTime = punchIn.ts;
        const endTime = punchOut.ts;
        if (!startTime || !endTime) {
          return acc;
        }

        const durationHours = endTime.diff(startTime, "hours").hours;
        acc += durationHours;
      }

      return acc;
    }, 0);

  if (totalHours === 0) {
    return TOTAL_HOURS_DISPLAY_PLACEHOLDER;
  }

  // Remove trailing zeros: 13.0 -> "13", 13.50 -> "13.5", 13.75 -> "13.75"
  const formattedHours = parseFloat(totalHours.toFixed(2)).toString();

  return `${formattedHours} ${translate("LABEL_TOTAL_HOURS_SUFFIX")}`;
}

export function getWeekRangeFromDateTime(dateString: string): {
  weekStart: DateTime;
  weekEnd: DateTime;
} {
  const selectedDateTime = DateTime.fromISO(dateString).startOf("day");
  // Start week on Sunday
  const weekStart = selectedDateTime.minus({ days: selectedDateTime.weekday % 7 });
  const weekEnd = weekStart.plus({ days: 6 });
  return { weekStart, weekEnd };
}

export function getWeekRangeFromWeekId(weekId: string): {
  weekStart: DateTime;
  weekEnd: DateTime;
} {
  const [year, unsafeWeekNumber] = weekId.split("-");
  let weekNumber = unsafeWeekNumber;

  // Handle edge cases where the week number is out of range.
  if (Number(weekNumber) <= 1) weekNumber = "01";
  if (Number(weekNumber) >= 53) weekNumber = "52";

  // Calculate the start of the week
  const weekStart = DateTime.fromObject({
    weekYear: Number(year),
    weekNumber: Number(weekNumber),
  })
    .startOf("week")
    // Using -1 to since the default ISO starts the week on Monday, while we
    // define start of week to be Sunday.
    .minus({ days: 1 });

  const weekEnd = weekStart.plus({ days: 6 });

  return { weekStart, weekEnd };
}

/**
 * Checks if a given date is within the current week.
 */
export function isDateInCurrentWeek(params: {
  // Represents the current date and time
  now: DateTime;
  // Represents the date to determine if it is in the current week
  date: DateTime;
}): boolean {
  const { now, date } = params;

  const { weekStart, weekEnd } = getWeekRangeFromDateTime(now.toISO());

  // Make sure that the date to compare is in the same timezone as the now date.
  return date.setZone(now.zoneName) >= weekStart && date.setZone(now.zoneName) <= weekEnd;
}
